// src/messaging/admin/util.js
// 先生・教務・運営者向けのメッセージ画面で共通に使う小物【2026-10-16】
//   ・日時は必ず日本時間（Asia/Tokyo）で出す。端末の時計の地域に左右されないように
//   ・予約の入力欄（datetime-local）は「日本時間の YYYY-MM-DDTHH:mm」として扱う
//   ・画像は送る前に端末で縮める（長い辺 2048px・JPEG 0.85）。位置情報などの埋め込み情報も落ちる

const TZ = 'Asia/Tokyo';
const JST_OFFSET_MS = 9 * 3600 * 1000;
export const MAX_SCHEDULE_DAYS = 60;
export const MAX_TEXT = 4000;
export const MAX_PDF_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** 2026/10/16(金) 09:05 */
export function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ja-JP', {
    timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric',
    weekday: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** 2026/10/16(金) */
export function fmtDate(v) {
  if (!v) return '';
  // 'YYYY-MM-DD' は日本時間の日付としてそのまま読む
  const d = /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? new Date(v + 'T00:00:00+09:00') : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('ja-JP', { timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' });
}

/** ISO → 日本時間の 'YYYY-MM-DDTHH:mm'（datetime-local の値） */
export function isoToJstInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() + JST_OFFSET_MS).toISOString().slice(0, 16);
}

/** 日本時間の 'YYYY-MM-DDTHH:mm' → ISO（空なら null） */
export function jstInputToIso(v) {
  if (!v) return null;
  const d = new Date(v + ':00+09:00');
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** 今日の日付（日本時間）'YYYY-MM-DD' */
export function todayJst() {
  return new Date(Date.now() + JST_OFFSET_MS).toISOString().slice(0, 10);
}

/** 予約欄の min / max（今〜60日後） */
export function scheduleBounds() {
  const now = Date.now();
  return {
    min: new Date(now + JST_OFFSET_MS).toISOString().slice(0, 16),
    max: new Date(now + MAX_SCHEDULE_DAYS * 86400000 + JST_OFFSET_MS).toISOString().slice(0, 16),
  };
}

/** 予約の入力を確かめる。問題があれば文、無ければ null */
export function checkSchedule(v) {
  const iso = jstInputToIso(v);
  if (!iso) return '送る日時を入れてください';
  const t = new Date(iso).getTime();
  if (t <= Date.now()) return '送る日時は今より後にしてください';
  if (t > Date.now() + MAX_SCHEDULE_DAYS * 86400000) return `予約は${MAX_SCHEDULE_DAYS}日先までです`;
  return null;
}

/** バイト数を読みやすく（1.2 GB / 340 MB / 12 KB） */
export function fmtBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1024 ** 3) return (v / 1024 ** 3).toFixed(v >= 100 * 1024 ** 3 ? 0 : 1) + ' GB';
  if (v >= 1024 ** 2) return (v / 1024 ** 2).toFixed(v >= 100 * 1024 ** 2 ? 0 : 1) + ' MB';
  if (v >= 1024) return Math.round(v / 1024) + ' KB';
  return v + ' B';
}

/** API の失敗を画面に出す文にする */
export function errText(r, fallback = 'うまくいきませんでした。少し待ってからもう一度試してください') {
  if (!r) return fallback;
  return r.message || fallback;
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ img, done: () => URL.revokeObjectURL(url) }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('画像を読み込めませんでした')); };
    img.src = url;
  });
}

/**
 * 画像を縮めて JPEG にする（長い辺 maxSide px、画質 quality）。
 *   読めない形式（HEIC など）のときは元のファイルをそのまま返す（サーバー側でも確かめる）。
 *   透明な部分は白で塗る（JPEG は透明を持てないため）。
 */
export async function downscaleImage(file, maxSide = 2048, quality = 0.85) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  let src;
  try {
    src = await loadImageElement(file);
  } catch (_) {
    return file;
  }
  try {
    const w0 = src.img.naturalWidth, h0 = src.img.naturalHeight;
    if (!w0 || !h0) return file;
    const scale = Math.min(1, maxSide / Math.max(w0, h0));
    const w = Math.max(1, Math.round(w0 * scale)), h = Math.max(1, Math.round(h0 * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(src.img, 0, 0, w, h);
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    if (!blob) return file;
    const base = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], base + '.jpg', { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    src.done();
  }
}

/**
 * 付けるファイルを確かめて、送れる形にする。
 *   返り値: { file } または { error }
 */
export async function prepareAttachment(file) {
  if (!file) return { file: null };
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')) {
    if (file.size > MAX_PDF_BYTES) return { error: 'PDF は 10MB までです' };
    return { file: file.type === 'application/pdf' ? file : new File([file], file.name, { type: 'application/pdf' }) };
  }
  if (!file.type || !file.type.startsWith('image/')) return { error: '付けられるのは画像か PDF だけです' };
  const small = await downscaleImage(file);
  if (small.size > MAX_IMAGE_BYTES) return { error: '画像が大きすぎます（5MB まで）' };
  return { file: small };
}
